export const RECENT_POST_SCHEDULED_TIME_CHANGE_TTL_MS = 5_000

type RecentPostScheduledTimeChange = {
  dueAt: string | null
  expiresAt: number
}

type PostScheduledTimeSnapshot = {
  dueAt: string | null
}

type ReconciledPostScheduledTimeEcho = {
  dueAt: string | null
  dueAtForCache: string | null
  isStale: boolean
}

const recentPostScheduledTimeChanges = new Map<
  string,
  RecentPostScheduledTimeChange
>()

export function rememberPostScheduledTimeChange(
  id: string,
  dueAt: string | null,
): void {
  pruneExpiredPostScheduledTimeChanges()
  recentPostScheduledTimeChanges.set(id, {
    dueAt,
    expiresAt: Date.now() + RECENT_POST_SCHEDULED_TIME_CHANGE_TTL_MS,
  })
}

export function getRecentPostScheduledTimeChange(
  id: string,
): RecentPostScheduledTimeChange | null {
  const change = recentPostScheduledTimeChanges.get(id)
  if (!change) return null

  if (change.expiresAt <= Date.now()) {
    recentPostScheduledTimeChanges.delete(id)
    return null
  }

  return change
}

export function forgetPostScheduledTimeChange(id: string): void {
  recentPostScheduledTimeChanges.delete(id)
}

export function shouldIgnoreStalePostScheduledTimeEcho(
  id: string,
  dueAt: string | null | undefined,
): boolean {
  const change = getRecentPostScheduledTimeChange(id)
  if (!change) return false

  const echoedDueAt = dueAt ?? null
  if (echoedDueAt === change.dueAt) {
    forgetPostScheduledTimeChange(id)
    return false
  }

  return true
}

export function reconcilePostScheduledTimeEcho(
  id: string,
  echoedDueAt: string | null,
  persistedPost: PostScheduledTimeSnapshot | null,
  visiblePost: PostScheduledTimeSnapshot | null,
): ReconciledPostScheduledTimeEcho {
  const isStale = shouldIgnoreStalePostScheduledTimeEcho(id, echoedDueAt)

  if (!isStale) {
    return {
      dueAt: echoedDueAt,
      dueAtForCache: echoedDueAt,
      isStale: false,
    }
  }

  return {
    // Use the optimistic view for filtering and connection decisions, while
    // preserving the persisted value in the base layer. If the optimistic
    // mutation rolls back, Apollo can then reveal the last confirmed date.
    dueAt: visiblePost ? visiblePost.dueAt : echoedDueAt,
    dueAtForCache: persistedPost ? persistedPost.dueAt : echoedDueAt,
    isStale: true,
  }
}

export function clearRecentPostScheduledTimeChanges(): void {
  recentPostScheduledTimeChanges.clear()
}

function pruneExpiredPostScheduledTimeChanges(): void {
  const now = Date.now()
  for (const [id, change] of recentPostScheduledTimeChanges.entries()) {
    if (change.expiresAt <= now) {
      recentPostScheduledTimeChanges.delete(id)
    }
  }
}
